import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { errorHandl } from 'src/app/utils/network-utils';
import { PlacementAuthService } from '../placement-auth-service/placement-auth.service';

@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  private http: HttpClient;

  constructor(private handler: HttpBackend, private placementAuthService: PlacementAuthService) {
    // Using HttpBackend to bypass interceptors that might overwrite the Authorization header
    this.http = new HttpClient(handler);
  }

  getReservations(): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.get<any>(`${this.placementAuthService.baseUrl}/responsible-parties/reservations`, { headers });
      }),
      catchError(errorHandl)
    );
  }

  getReservationDetails(reservationId: number | string): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.get<any>(`${this.placementAuthService.baseUrl}/patients/reservations/${reservationId}`, { headers });
      }),
      catchError(errorHandl)
    );
  }

  acceptRates(intakeId: number | string): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.post<any>(
          `${this.placementAuthService.baseUrl}/patients/reservations/acceptRates/${intakeId}`,
          {},
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  declineRates(intakeId: number | string): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.post<any>(
          `${this.placementAuthService.baseUrl}/patients/reservations/declineRates/${intakeId}`,
          {},
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  uploadPhysicianReport(reservationId: number | string, file: File): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const formData = new FormData();
        formData.append('pdf', file, file.name);
        return this.http.put<any>(
          `${this.placementAuthService.baseUrl}/facility-owner/reservations/form/${reservationId}`,
          formData,
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  sendFormRequest(onboardingId: number | string, payload: any, usePut: boolean = false): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const url = `${this.placementAuthService.baseUrl}/agents/form-request/${onboardingId}`;
        const request$ = usePut 
          ? this.http.put<any>(url, payload, { headers })
          : this.http.post<any>(url, payload, { headers });
        return request$;
      }),
      catchError(errorHandl)
    );
  }

  declineReservation(onboardingId: number | string): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.post<any>(
          `${this.placementAuthService.baseUrl}/facility-owner/reservations/decline/${onboardingId}`,
          {},
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  getReservationNotes(reservationId: number | string): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.get<any>(
          `${this.placementAuthService.baseUrl}/patients/reservations/${reservationId}/notes`,
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  addReservationNote(reservationId: number | string, payload: { notes: string }): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.post<any>(
          `${this.placementAuthService.baseUrl}/patients/reservations/${reservationId}/notes`,
          payload,
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }

  submitReview(referenceId: string, payload: any): Observable<any> {
    return this.placementAuthService.login().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        return this.http.post<any>(
          `${this.placementAuthService.baseUrl}/patients/reservations/reviews/${referenceId}`,
          payload,
          { headers }
        );
      }),
      catchError(errorHandl)
    );
  }
}
